import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SearchableSelect.css';

export interface SelectOption {
  value: number;
  label: string;
  subLabel?: string;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value: number | null;
  onChange: (value: number, option: SelectOption | undefined) => void;
  onSearch?: (searchText: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  required?: boolean;
}

const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

const IconChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6,9 12,15 18,9"></polyline>
  </svg>
);

const IconX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  onSearch,
  placeholder = 'Seleziona...',
  disabled = false,
  loading = false,
  label,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Trova l'opzione selezionata
  const selectedOption = options.find(opt => opt.value === value);

  // Filtra le opzioni localmente se non c'� onSearch
  const filteredOptions = onSearch 
    ? options 
    : options.filter(opt => 
        opt.label.toLowerCase().includes(searchText.toLowerCase()) ||
        (opt.subLabel && opt.subLabel.toLowerCase().includes(searchText.toLowerCase()))
      );

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Gestisci la ricerca con debounce
  useEffect(() => {
    if (!onSearch || !isOpen) return;

    const timer = setTimeout(() => {
      onSearch(searchText);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText, onSearch, isOpen]);

  // Scroll all'elemento evidenziato
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('.select-option');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(-1);
    setTimeout(() => inputRef.current?.focus(), 10);
    // Carica opzioni iniziali
    if (onSearch) {
      onSearch('');
    }
  };

  const handleSelect = (option: SelectOption) => {
    onChange(option.value, option);
    setIsOpen(false);
    setSearchText('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(0, undefined);
    setSearchText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleOpen();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchText('');
        break;
    }
  };

  return (
    <div className={`searchable-select ${disabled ? 'disabled' : ''}`} ref={containerRef}>
      {label && (
        <label className="select-label">
          {label} {required && '*'}
        </label>
      )}
      
      <div 
        className={`select-control ${isOpen ? 'open' : ''}`}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {isOpen ? (
          <div className="select-search">
            <IconSearch />
            <input
              ref={inputRef}
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Cerca..."
              className="search-input"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ) : (
          <div className="select-value">
            {selectedOption ? (
              <span className="selected-text">{selectedOption.label}</span>
            ) : (
              <span className="placeholder-text">{placeholder}</span>
            )}
          </div>
        )}

        <div className="select-icons">
          {value && !isOpen && (
            <button 
              type="button" 
              className="clear-btn" 
              onClick={handleClear}
              aria-label="Cancella selezione"
            >
              <IconX />
            </button>
          )}
          <span className={`chevron ${isOpen ? 'open' : ''}`}>
            <IconChevronDown />
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="select-dropdown" ref={listRef} role="listbox">
          {loading ? (
            <div className="select-loading">
              <span className="loading-spinner"></span>
              Caricamento...
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="select-empty">
              {searchText ? 'Nessun risultato' : 'Nessuna opzione'}
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className={`select-option ${option.value === value ? 'selected' : ''} ${index === highlightedIndex ? 'highlighted' : ''}`}
                onClick={() => handleSelect(option)}
                role="option"
                aria-selected={option.value === value}
              >
                <span className="option-label">{option.label}</span>
                {option.subLabel && (
                  <span className="option-sublabel">{option.subLabel}</span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
